/**
 * GoogleReCaptchaProvider's own cleanup (react-google-recaptcha-v3) removes
 * its script tag and any *currently existing* `.grecaptcha-badge` on
 * unmount, but it never invalidates the `onLoadCallbackName` function it
 * registered on `window`. If Google's api.js is still loading when the
 * provider unmounts - e.g. the user clicks from /login to Home before the
 * script has finished initializing - that stale callback can still fire
 * afterwards and call `grecaptcha.render()` again, recreating the badge on
 * whatever page happens to be on screen at that moment.
 *
 * Each AuthLayout mount gets its own instance id, so its script tag, render
 * container and load-callback name are unique to that mount: a callback
 * left over from a previous (unmounted) instance can never collide with, or
 * be mistaken for, the current one.
 */
let recaptchaInstanceCounter = 0;

export function nextRecaptchaInstanceId(): number {
  recaptchaInstanceCounter += 1;
  return recaptchaInstanceCounter;
}

export function recaptchaScriptId(instanceId: number): string {
  return `google-recaptcha-v3-${instanceId}`;
}

export function recaptchaLoadCallbackName(instanceId: number): string {
  return `onRecaptchaLoadCallback_${instanceId}`;
}

export function recaptchaContainerId(instanceId: number): string {
  return `recaptcha-badge-${instanceId}`;
}

/**
 * Overwrites the provider's load callback with a no-op. Must run
 * synchronously from the unmounting effect's cleanup so it is already in
 * place before any in-flight script load can reach it - a stale call then
 * does nothing instead of rendering a fresh, unscoped badge into the body.
 */
export function invalidateRecaptchaCallback(callbackName: string): void {
  if (typeof window === "undefined") return;

  (window as unknown as Record<string, unknown>)[callbackName] = () => {};
}
